import axios from 'axios';
import { getAccessToken } from '../services/auth.service';
import { useImpersonationStore } from '../stores/useImpersonationStore';
import { useAtlasAccessStore } from '../stores/useAtlasAccessStore';

const axiosInstance = axios.create({
  baseURL: window.location.origin,
  timeout: 90000,
});

// Request interceptor - add auth headers
axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config.url || '';

    // Skip auth headers for public endpoints
    const isAuthTokenCall = url.indexOf('oDataAuth/GetAuthToken') !== -1;

    if (!isAuthTokenCall) {
      const accessToken = await getAccessToken();

      if (accessToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      // Add impersonation header if superuser is impersonating
      const { impersonatedUser, isSuperuser } = useImpersonationStore.getState();
      if (isSuperuser && impersonatedUser) {
        config.headers['X-Impersonated-Email'] = impersonatedUser.email;
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors globally
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const errorCode = error.response?.data?.code;

    if (error.response?.status === 403 &&
        ['atlas_access_denied', 'atlas_user_missing', 'atlas_user_baja'].includes(errorCode)) {
      useAtlasAccessStore.getState().denyAccess(errorCode);
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      // Clear any stale tokens
      localStorage.removeItem('oDataAuthToken');

      const newToken = await getAccessToken(true);
      if (newToken) {
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      } else {
        // Token acquisition failed, redirect to login
        console.error('Authentication failed, redirecting to login');
        window.location.href = '/'; // MSAL will handle redirection
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
